import { useEffect, useRef, type RefObject } from "react";
import { flushSync } from "react-dom";
import { canvasNavigationBelongsToControl } from "./canvasNavigationTarget";

type Point = { x: number; y: number };
type WheelInput = "mouse" | "trackpad";

export type ScrollableCanvasNavigationOptions = {
  viewportRef: RefObject<HTMLElement | null>;
  canvasRef: RefObject<HTMLElement | null>;
  enabled?: boolean;
  zoom: number;
  minZoom: number;
  maxZoom: number;
  setZoom: (zoom: number) => void;
  chainScroll?: boolean;
};

/** Pass unused pan distance to the surrounding editor when the canvas reaches an edge. */
export function scrollCanvasWithOverflow(viewport: HTMLElement, deltaX: number, deltaY: number): void {
  const boundary = viewport.closest(".dockPanel");
  for (let element: HTMLElement | null = viewport; element; element = element.parentElement) {
    const style = getComputedStyle(element);
    if (element === viewport || ["auto", "scroll", "overlay"].includes(style.overflowX)) {
      const previous = element.scrollLeft;
      element.scrollLeft = Math.max(0, Math.min(Math.max(0, element.scrollWidth - element.clientWidth), previous + deltaX));
      deltaX -= element.scrollLeft - previous;
    }
    if (element === viewport || ["auto", "scroll", "overlay"].includes(style.overflowY)) {
      const previous = element.scrollTop;
      element.scrollTop = Math.max(0, Math.min(Math.max(0, element.scrollHeight - element.clientHeight), previous + deltaY));
      deltaY -= element.scrollTop - previous;
    }
    if (element === boundary || (!deltaX && !deltaY)) break;
  }
}

/**
 * Shared navigation for editors whose drawing is a real, resizable DOM canvas
 * inside a native scroll container. Zooming keeps the model point below the
 * cursor stable by restoring its relative position after the canvas resized.
 */
export function useScrollableCanvasNavigation(options: ScrollableCanvasNavigationOptions) {
  const optionsRef = useRef(options);
  const queueZoomRef = useRef<(value: number, clientAnchor?: Point) => void>();
  optionsRef.current = options;

  useEffect(() => {
    if (optionsRef.current.enabled === false) return;
    const viewport = optionsRef.current.viewportRef.current;
    const canvas = optionsRef.current.canvasRef.current;
    if (!viewport || !canvas) return;

    let currentWheelInput: WheelInput | undefined;
    let lastWheelTime = 0;
    let zoomFrame: number | null = null;
    let lastZoomTime: number | null = null;
    let pendingZoom = optionsRef.current.zoom;
    let pendingClientAnchor: Point | null = null;
    let touchStartDistance: number | null = null;
    let touchStartZoom = optionsRef.current.zoom;
    let lastTouchCenter: Point | null = null;
    let nativeGestureScale = 1;
    let nativeGestureZoom = optionsRef.current.zoom;
    let nativeGestureActive = false;

    const classifyWheelEvent = (event: WheelEvent): WheelInput => {
      const now = performance.now();
      if (now - lastWheelTime > 180) currentWheelInput = undefined;
      lastWheelTime = now;
      if (currentWheelInput) return currentWheelInput;
      if (event.deltaMode !== WheelEvent.DOM_DELTA_PIXEL) return (currentWheelInput = "mouse"); // no itx
      return (currentWheelInput = Math.abs(event.deltaX) > 0.5 || Math.abs(event.deltaY) < 40 ? "trackpad" : "mouse"); // no itx
    };

    const queueZoom = (value: number, clientAnchor?: Point) => {
      const currentOptions = optionsRef.current;
      pendingZoom = Math.min(currentOptions.maxZoom, Math.max(currentOptions.minZoom, value));
      pendingClientAnchor = clientAnchor ?? null;
      if (zoomFrame !== null) return;

      const animate = (time: number) => {
        const liveOptions = optionsRef.current;
        const currentZoom = liveOptions.zoom;
        const elapsed = lastZoomTime === null ? 16 : Math.min(50, time - lastZoomTime);
        const difference = pendingZoom - currentZoom;
        const progress = 1 - Math.exp(-elapsed / 55);
        const nextZoom = Math.abs(difference) < 0.0005 ? pendingZoom : currentZoom + difference * progress;
        const viewportRect = viewport.getBoundingClientRect();
        const screenAnchor = pendingClientAnchor
          ? { x: pendingClientAnchor.x - viewportRect.left, y: pendingClientAnchor.y - viewportRect.top }
          : { x: viewport.clientWidth / 2, y: viewport.clientHeight / 2 };
        const oldCanvasRect = canvas.getBoundingClientRect();
        const clientAnchor = {
          x: viewportRect.left + screenAnchor.x,
          y: viewportRect.top + screenAnchor.y,
        };
        const anchorRatio = {
          x: (clientAnchor.x - oldCanvasRect.left) / Math.max(oldCanvasRect.width, 1),
          y: (clientAnchor.y - oldCanvasRect.top) / Math.max(oldCanvasRect.height, 1),
        };

        flushSync(() => liveOptions.setZoom(nextZoom));
        const nextCanvasRect = canvas.getBoundingClientRect();
        viewport.scrollBy({
          left: nextCanvasRect.left + anchorRatio.x * nextCanvasRect.width - clientAnchor.x,
          top: nextCanvasRect.top + anchorRatio.y * nextCanvasRect.height - clientAnchor.y,
        });
        lastZoomTime = time;

        if (nextZoom === pendingZoom) {
          zoomFrame = null;
          lastZoomTime = null;
          pendingClientAnchor = null;
        } else {
          zoomFrame = requestAnimationFrame(animate);
        }
      };
      zoomFrame = requestAnimationFrame(animate);
    };
    queueZoomRef.current = queueZoom;

    const onWheel = (event: WheelEvent) => {
      if (!(event.target instanceof Node) || !viewport.contains(event.target)) return;
      if (canvasNavigationBelongsToControl(event.target, viewport)) {
        if (event.ctrlKey || event.metaKey) event.preventDefault();
        return;
      }
      event.preventDefault();
      if (classifyWheelEvent(event) === "trackpad" && !event.ctrlKey && !event.metaKey) {
        if (optionsRef.current.chainScroll) scrollCanvasWithOverflow(viewport, event.deltaX, event.deltaY);
        else {
          viewport.scrollLeft += event.deltaX;
          viewport.scrollTop += event.deltaY;
        }
        return;
      }
      if (event.deltaY === 0) return;
      const baseZoom = zoomFrame === null ? optionsRef.current.zoom : pendingZoom;
      const exponent = Math.max(-0.1, Math.min(0.1, -event.deltaY * 0.006));
      queueZoom(baseZoom * Math.exp(exponent), { x: event.clientX, y: event.clientY });
    };

    const distance = (touches: TouchList) =>
      Math.hypot(touches[1].clientX - touches[0].clientX, touches[1].clientY - touches[0].clientY);
    const center = (touches: TouchList) => ({
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    });
    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 2 || !(event.target instanceof Node) || !viewport.contains(event.target)) return;
      touchStartDistance = distance(event.touches);
      touchStartZoom = optionsRef.current.zoom;
      lastTouchCenter = center(event.touches);
    };
    const onTouchMove = (event: TouchEvent) => {
      if (nativeGestureActive || event.touches.length !== 2 || !touchStartDistance) return;
      event.preventDefault();
      const nextCenter = center(event.touches);
      if (lastTouchCenter) {
        const deltaX = lastTouchCenter.x - nextCenter.x;
        const deltaY = lastTouchCenter.y - nextCenter.y;
        if (optionsRef.current.chainScroll) scrollCanvasWithOverflow(viewport, deltaX, deltaY);
        else {
          viewport.scrollLeft += deltaX;
          viewport.scrollTop += deltaY;
        }
      }
      lastTouchCenter = nextCenter;
      queueZoom(touchStartZoom * (distance(event.touches) / touchStartDistance), nextCenter);
    };
    const resetTouch = () => {
      touchStartDistance = null;
      lastTouchCenter = null;
    };
    const onGestureStart = (event: Event) => {
      event.preventDefault();
      nativeGestureActive = true;
      nativeGestureScale = (event as Event & { scale?: number }).scale ?? 1;
      nativeGestureZoom = optionsRef.current.zoom;
      resetTouch();
    };
    const onGestureChange = (event: Event) => {
      event.preventDefault();
      const scale = (event as Event & { scale?: number }).scale ?? nativeGestureScale;
      queueZoom(nativeGestureZoom * (scale / nativeGestureScale));
    };
    const onGestureEnd = (event: Event) => {
      event.preventDefault();
      nativeGestureActive = false;
    };

    viewport.addEventListener("wheel", onWheel, { passive: false });
    viewport.addEventListener("touchstart", onTouchStart, { passive: true });
    viewport.addEventListener("touchmove", onTouchMove, { passive: false });
    viewport.addEventListener("touchend", resetTouch);
    viewport.addEventListener("touchcancel", resetTouch);
    viewport.addEventListener("gesturestart", onGestureStart, { passive: false });
    viewport.addEventListener("gesturechange", onGestureChange, { passive: false });
    viewport.addEventListener("gestureend", onGestureEnd, { passive: false });
    return () => {
      queueZoomRef.current = undefined;
      if (zoomFrame !== null) cancelAnimationFrame(zoomFrame);
      viewport.removeEventListener("wheel", onWheel);
      viewport.removeEventListener("touchstart", onTouchStart);
      viewport.removeEventListener("touchmove", onTouchMove);
      viewport.removeEventListener("touchend", resetTouch);
      viewport.removeEventListener("touchcancel", resetTouch);
      viewport.removeEventListener("gesturestart", onGestureStart);
      viewport.removeEventListener("gesturechange", onGestureChange);
      viewport.removeEventListener("gestureend", onGestureEnd);
    };
  }, [options.canvasRef, options.viewportRef, options.enabled]);

  return {
    zoomTo: (zoom: number, clientAnchor?: Point) => queueZoomRef.current?.(zoom, clientAnchor),
  };
}

export type ScrollableCanvasPan = {
  pointerId: number;
  clientX: number;
  clientY: number;
  scrollLeft: number;
  scrollTop: number;
};

export function createScrollableCanvasPan(viewport: HTMLElement, pointerId: number, clientX: number, clientY: number) {
  return { pointerId, clientX, clientY, scrollLeft: viewport.scrollLeft, scrollTop: viewport.scrollTop };
}

export function updateScrollableCanvasPan(viewport: HTMLElement, pan: ScrollableCanvasPan, clientX: number, clientY: number) {
  viewport.scrollLeft = pan.scrollLeft - (clientX - pan.clientX);
  viewport.scrollTop = pan.scrollTop - (clientY - pan.clientY);
}
